import React from 'react';
import { find, get, isEmpty, merge, head, isEqual, filter, debounce, isUndefined } from 'lodash';
import queryString from 'query-string';

// local dependies
import { memberDetailAssistiveActionsNavWidget as MemberDetailAssistiveActionsNavWidgetView } from 'ui/views/widgets/memberDetailAssistiveActionsNavWidget';
import { MemberDetailAssistiveActionsNavWidgetService } from 'services/memberDetailAssistiveActionsNavWidget';
import { SERVICE_RESPONSE_STATUS, SIZES, GLOBAL_LABELS } from 'constants/common';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { withRouter } from 'react-router-dom';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { messageBus } from 'core/modules/message-bus';
import { resolveSecondaryOptionsResolver } from 'resolvers/resolveSecondaryOptions';
import * as ROUTE_PATHS from 'constants/route-paths';
import { getAppSyncData } from 'utils/getAppSyncData';
import { resolveMemberDetailSelection } from 'utils/memberDetailSelection';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailAssistiveActionsNavWidget } from 'ui/containers/MemberDetailAssistiveActionsNavWidget';
 *
 * // render
 * <MemberDetailAssistiveActionsNavWidget propName='propValue'/>
 */
class _MemberDetailAssistiveActionsNavWidget extends React.Component {

    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.handleActionClick = this.handleActionClick.bind( this );
        this.switchDrugPreference = this.switchDrugPreference.bind( this );
        this.deactivateInEpost = this.deactivateInEpost.bind( this );
        this.reactivateInEpost = this.reactivateInEpost.bind( this );
        this.submitDeactivateEpost = debounce( this.submitDeactivateEpost, 500 ).bind( this );
        this.impersonateMember = this.impersonateMember.bind( this );
        this.fetchPriorAuthorization = this.fetchPriorAuthorization.bind( this );
        this.fetchOutboundCalls = this.fetchOutboundCalls.bind( this );
        this.fetchWebsiteIssues = this.fetchWebsiteIssues.bind( this );
        this.fetchMemberEscalationCall = this.fetchMemberEscalationCall.bind( this );
        this.fetchMaterialMailOut = this.fetchMaterialMailOut.bind( this );
        this.setPatientNotes = this.setPatientNotes.bind( this );
        this.submitLangPreference = this.submitLangPreference.bind( this );

        // create service instance
        this.memberDetailAssistiveActionsNavWidgetService = new MemberDetailAssistiveActionsNavWidgetService();

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined
        };

        this.fetchComponentData();

    }

    async fetchComponentData() {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchCompoenentData()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // fetch component data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchComponentData( {
            url: this.props.widget.url,
            data: this.props.requestPayload
        } );

        // check if response status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );

        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    /**
     * @desc Switch drug preferences.
     */
    async switchDrugPreference( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.switchDrugPreference()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchSwitchDrugPreferernceData( {
            url: url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.SET_DRUG_PREFERENCE,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    onSumbitClick: async ( value ) => {

                        overlay.close();

                        // find action with `name: submit`
                        const action = find( get( data, 'actions', [] ), { name: 'submit' } );

                        // cancel ongoing request
                        this.memberDetailAssistiveActionsNavWidgetService.cancel();

                        // show page level preloader
                        pageLevelPreloader.show();

                        // get status and service data
                        const { _status } = await this.memberDetailAssistiveActionsNavWidgetService.setDrugPreference( {
                            url: action.url,
                            data: {
                                ...this.props.requestPayload,
                                data: {
                                    setCurrentDrugPreference: value,
                                    drugPreference: get( data, 'values.drugPreference', null )
                                }
                            }
                        } );

                        // show page level preloader
                        pageLevelPreloader.hide();

                        if ( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                            successOverlay.open( {
                                message: getLabel( action.onSuccess ),
                            } );
                        } else {
                            errorOverlay.open();
                        }

                    },
                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    async addCreditAction( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.switchDrugPreference()' );
        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchSwitchDrugPreferernceData( {
            url: url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.ADD_CREDIT_OVERLAY,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    ...this.props.requestPayload,
                    onSumbitClick: async ( value ) => {

                        overlay.close();

                        // find action with `name: submit`
                        const action = find( get( data, 'actions', [] ), { name: 'submit' } );

                        // cancel ongoing request
                        this.memberDetailAssistiveActionsNavWidgetService.cancel();

                        // show page level preloader
                        pageLevelPreloader.show();

                        // get status and service data
                        const { _status } = await this.memberDetailAssistiveActionsNavWidgetService.setDrugPreference( {
                            url: action.url,
                            data: {
                                ...this.props.requestPayload
                            }
                        } );

                        // show page level preloader
                        pageLevelPreloader.hide();

                        if ( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                            successOverlay.open( {
                                message: getLabel( action.onSuccess ),
                            } );
                        } else {
                            errorOverlay.open();
                        }

                    },
                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc Prior Authorization Form.
     */
    async fetchPriorAuthorization( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchPriorAuthorization()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchPriorAuthorizationData( {
            url: url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            log.debug( data );
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.PRIOR_AUTHORIZATION,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    requestPayload: this.props.requestPayload,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc Outbound Calls Form.
     */
    async fetchOutboundCalls( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchOutboundCalls()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchOutboundCallsData( {
            url: url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            log.debug( data );
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.OUTBOUND_CALLS,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    requestPayload: this.props.requestPayload,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc Website Issues Form.
     */
    async fetchWebsiteIssues( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchWebsiteIssues()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchWebsiteIssuesData( {
            url: url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            log.debug( data );
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.WEBSITE_ISSUES,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    requestPayload: this.props.requestPayload,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc Member Escalation Call Form.
     */
    async fetchMemberEscalationCall( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchMemberEscalationCall()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchMemberEscalationCallData( {
            url: url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.MEMBER_ESCALATION_CALL,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...this.props.requestPayload,
                    ...data,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc Handle Form Change.
     */
    handleFormChangeCallback( { values } ) {
        log.debug( 'MemberDetailsAssistiveActionsNavWidget.handleFormChangeCallback()' );

        /*-------------------*/

        const primaryTypes = filter( this.props.fields, field => {
            return get( field, 'config.secondaryOptions' );
        } );

        const primaryTypeField = head( primaryTypes );
        const primaryType = get( values, `${ primaryTypeField.name }`, undefined );

        if( ! isEmpty( primaryType ) && ! isEqual( primaryType, this.state.primaryType ) ) {

            const { updatedOptions } = resolveSecondaryOptionsResolver( {
                values,
                options: this.props.options,
                primaryTypeField,
                fields: this.state.fields
            } );

            this.setState( {
                values: values,
                options: updatedOptions
            } );
        }

        /*-------------------*/
    }

    /**
     * @desc Handles initiation of deactivate epost request
     */
    async deactivateInEpost( { url, /*confirmation,*/ onSubmitUrl } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.deactivateInEpost()' );

        // show page level preloader
        pageLevelPreloader.show();

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.deactivateInEpost( {
            url: url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.DEACTIVATE_EPOST,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    onSumbitAction: ( selectedVal ) => {

                        // close overlay
                        overlay.close();

                        // show page level preloader
                        pageLevelPreloader.show();

                        // submit deactivate epost request
                        this.submitDeactivateEpost( selectedVal, onSubmitUrl );
                    }
                }
            } );

        } else {

            errorOverlay.open();
        }

    }


    async reactivateInEpost( { url, /*confirmation,*/ onSubmitUrl } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.reactivateInEpost()' );

        // show page level preloader
      //  pageLevelPreloader.show();

        // cancel ongoing request
        //this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // get status and service data
        // const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.reactivateInEpost( {
        //     url: url,
        //     data: this.props.requestPayload
        // } );

        // // show page level preloader
        // pageLevelPreloader.hide();

        // if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.REACTIVATE_EPOST,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                // config: {},
                // data: {
                //     ...data,
                   
                // }
            } );

        // } else {

        //     errorOverlay.open();
        // }

    }

    /**
     * @desc Handles submission of deactivate epost request
     */
    async submitDeactivateEpost( selectedVal, onSubmitUrl ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.submitDeactivateEpost()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // get status and service data
        const { _status, resData } = await this.memberDetailAssistiveActionsNavWidgetService.sumbitDeActivateEpost( {
            url: onSubmitUrl,
            data: merge( {
                ...this.props.requestPayload,
                data: {
                    reason: selectedVal
                }
            } )
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

            this.setState( {
                componentData: {
                    ...this.state.componentData,
                    visibility: {
                        ...this.state.componentData.visibility,
                        deactivateInEpostAction: false,
                        reactivateEpostAction: true,
                    }
                }
            }, () => {

                // display success overlay
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );

                // trigger event to refresh modules
                messageBus.trigger( MESSAGE_BUS_EVENTS.MEMBER_MANAGEMENT_REFRESH );

            } );

        } else {

            if ( !isEmpty( resData.description ) ) {

                if ( !isEmpty( get( resData, 'fields', {} ) && !isEmpty( get( resData, 'values', {} ) ) ) ) {

                    errorOverlay.open( {
                        message: resData.description,
                        size: SIZES.SMALL,
                        templateType: 'fields',
                        data: {
                            values: resData.values,
                            fields: resData.fields,
                        }
                    } );

                } else {

                    errorOverlay.open( {
                        title: resData.title,
                        message: resData.description,
                        size: SIZES.SMALL,
                        template: resData.template
                    } );
                }

            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc Handles submission of laguage preference request
     */
     async submitLangPreference( selectedVal, onSubmitUrl ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.submitLangPreference()' );

        const reqData = {};

        if ( !isEmpty( selectedVal ) ) {
            reqData [ selectedVal.name ] = selectedVal.value;
        }

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // get status and service data
        const { _status, resData } = await this.memberDetailAssistiveActionsNavWidgetService.submitLanguagePreference( {
            url: onSubmitUrl,
            data: merge( {
                ...this.props.requestPayload,
                data: {
                    ...reqData
                }
            } )
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

            // display success overlay
            successOverlay.open( {
                message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY )
                
                // settings: {
                //     onClose: () => this.props.history.push( ROUTE_PATHS.DASHBOARD )
                // }
            } );

        } else {

            errorOverlay.open( {
                title: resData.title,
                message: resData.description,
                size: SIZES.SMALL,
                template: resData.template
            } );
        }
    }

    /**
     * @desc Deactivate Epost.
     */
    async impersonateMember( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.impersonateMember()' );

        // const memberId = get( this.props.location, 'state.memberId', undefined );

        // Had to change from above line to this because of defect 41571
        const memberId = isUndefined( this.props.location.state ) ? getAppSyncData( 'memberId' ) : get( this.props.location, 'state.memberId', undefined );
        const patientId = isUndefined( this.props.location.state ) ? getAppSyncData( 'patientId' ) : get( this.props.location, 'state.patientId', undefined );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.impersonateMember( {
            url: url,
            data: {
                context: {
                    memberId: memberId,
                    patientId: patientId,
                }
            }
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        // on success, open an overlay with SAML SSO form
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.IMPERSONATE_MEMBER,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: data,
            } );
        } else {

            // show error dialog
            if ( !isEmpty( data.description ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }

    }

    /**
     * @desc Create MSR Notes.
     */
    async epostMSRNotes( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.epostMSRNotes()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.epostMSRNotes( {
            url: url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        const requestPayload = this.props.requestPayload;
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.CSR_MEMBER_NOTES,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    action: find( get( data, 'structure.formActions', [] ), { name: 'save' } ),
                    url,
                    data,
                    requestPayload,

                    // onSubmitClick: async ( value ) => {

                    //     // overlay.close();

                    //     // find action with `name: submit`
                    //     const action = find( get( data, 'structure.formActions', [] ), { name: 'save' } );

                    //     // cancel ongoing request
                    //     this.memberDetailAssistiveActionsNavWidgetService.cancel();

                    //     // show page level preloader
                    //     pageLevelPreloader.show();

                    //     // get status and service data
                    //     const { _status, resData } = await this.memberDetailAssistiveActionsNavWidgetService.addNotes( {
                    //         url: action.url,
                    //         data: {
                    //             ...this.props.requestPayload,
                    //             data: {
                    //                 ...value
                    //             }
                    //         }
                    //     } );

                    //     // show page level preloader
                    //     pageLevelPreloader.hide();

                    //     if( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                    //         // this.fetchNotes( { url } );

                    //         // this.epostMSRNotes( { url } );

                    //     } else {

                    //         if( ! isEmpty( get( resData, 'description' ) ) ) {
                    //             errorOverlay.open( { message: resData.description, size: SIZES.SMALL, } );
                    //         } else {
                    //             errorOverlay.open();
                    //         }
                    //     }
                    // },
                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc executes when user clicks on business process to-do action
     */
    async businessProcessTodo( { action } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.businessProcessTodo()' );

        pageLevelPreloader.show();

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        try {
            const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchBusinessProcessTodoInfo( {
                url: action.url,
                data: this.props.requestPayload,
            } );

            pageLevelPreloader.hide();

            // check if response status was success
            if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                errorOverlay.open();

            } else {

                // open overlay and send the `data`
                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.MEMBER_BUSINESS_PROCESS,
                        showCloseButton: false,
                        ignoreOutsideClick: false,
                    },
                    config: {},
                    data: {
                        ...this.props.requestPayload,
                        ...action,
                        ...data,
                        onCancelClick: () => {
                            overlay.close();
                        },
                    }
                } );
            }

        } catch ( e ) {

            pageLevelPreloader.hide();

            errorOverlay.open();
        }
    }

    /**
     * @desc executes when user clicks on Member Services Escalation action
     */
    async memberServicesEscalations( { action } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.memberServicesEscalations()' );

        pageLevelPreloader.show();

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        try {
            const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchMemberServicesEscalationsInfo( {
                url: action.url,
                data: this.props.requestPayload,
            } );

            pageLevelPreloader.hide();

            // check if response status was success
            if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                errorOverlay.open();

            } else {

                // open overlay and send the `data`
                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.MEMBER_SERVICES_ESCALATIONS,
                        showCloseButton: false,
                        ignoreOutsideClick: false,
                    },
                    config: {},
                    data: {
                        ...this.props.requestPayload,
                        ...action,
                        ...data,
                        onCancelClick: () => {
                            overlay.close();
                        },
                    }
                } );
            }

        } catch ( e ) {

            pageLevelPreloader.hide();

            errorOverlay.open();
        }
    }

    /**
     * @desc Executes when MSR clicks on the (Material Mail Out TO-DO Form).
     */
    async fetchMaterialMailOut( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchMaterialMailOut()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchMaterialMailOutData( {
            url: url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.MATERIAL_MAIL_OUT,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...this.props.requestPayload,
                    ...data,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc Executes when MSR clicks on the Status of Claim.
     */
    async fetchStatusOfClaim( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchStatusOfClaim()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchStatusOfClaimTodoInfo( {
            url: url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.STATUS_OF_CLAIM,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...this.props.requestPayload,
                    ...data,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error overlay
        }

    }

    /**
     * @desc Executes when MSR clicks on the Add Insurance/Discount Card action.
     */
    async fetchAddInsuranceDiscountCardTodoInfo( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchAddInsuranceDiscountCardTodoInfo()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchAddInsuranceDiscountCardTodoInfo( {
            url: url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.ADD_INSURANCE_DISCOUNT_CARD,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...this.props.requestPayload,
                    ...data,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error overlay
        }

    }

    /**
     * @desc Executes when MSR clicks on Patient Notes.
     */
    async setPatientNotes( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.setPatientNotes()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.setPatientNotesData( {
            url: url,
            _data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.SET_PATIENT_NOTES,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...this.props.requestPayload,
                    ...data,
                    url,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /**
     * @desc Executes when MSR clicks on CMS action
     */
     async fetchCmsWidgetInfo( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchCmsWidgetInfo()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchCmsWidgetInfo( {
            url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.CMS_WIDGET,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                data: {
                    ...this.props.requestPayload,
                    ...data
                }
            } );
        } else {
            errorOverlay.open(); // show error overlay
        }

    }

    /**
     * @desc Executes when MSR clicks on Language preference
     */
     async fetchLangPreferenceWidgetInfo( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchLangPreferenceWidgetInfo()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchLanguagePreferenceWidgetInfo( {
            url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.LANGUAGE_PREFERENCE,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                data: {
                    ...this.props.requestPayload,
                    ...data,
                    onFormSubmit: ( selectedVal, onSubmitUrl ) => {

                        // close overlay
                        overlay.close();
    
                        // show page level preloader
                        pageLevelPreloader.show();
    
                        // submit language preference
                        this.submitLangPreference( selectedVal, onSubmitUrl );
                    }
                }
            } );
        } else {
            errorOverlay.open(); // show error overlay
        }

    }

    /**
     * @desc Executes when MSR clicks on personal Id action
     */
     async fetchPersonalIdInfo( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchPersonalIdInfo()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchPersonalIdWidgetInfo( {
            url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.PERSONAL_ID_WIDGET,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                data: {
                    ...this.props.requestPayload,
                    ...data
                }
            } );
        } else {
            errorOverlay.open(); // show error overlay
        }

    }

    async fetchivrPin( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.ivrPin()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.ivrPin( {
            url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.IVR_GET_PIN,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                data: {
                    ...this.props.requestPayload,
                    ...data
                }
            } );
        } else {
            errorOverlay.open(); // show error overlay
        }

    }


    /**
     * @desc Executes when MSR clicks on Service Now action
     */
     async fetchServiceNowForm( { url } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.fetchServiceNowForm()' );

        // cancel ongoing request
        this.memberDetailAssistiveActionsNavWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberDetailAssistiveActionsNavWidgetService.fetchServiceNowForm( {
            url,
            data: this.props.requestPayload
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.SERVICE_NOW,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                data: {
                    ...this.props.requestPayload,
                    ...data
                }
            } );
        } else {
            errorOverlay.open(); // show error overlay
        }
    }

    /**
     * @desc Handle action click.
     */
    handleActionClick( { value } ) {
        log.debug( 'MemberDetailAssistiveActionsNavWidget.handleActionClick()' );
        log.debug( value.name );

        if ( 'switchDrugPreference' === value.name ) {
            this.switchDrugPreference( {
                url: value.url
            } );
        } else if ( 'drugPricing' === value.name ) {

            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.DRUG_SEARCH,
                state: { memberId: get( this.props.location, 'state.memberId', undefined ), patientId:  get( this.props.location, 'state.patientId', undefined ) }
            } );

        } else if ( 'deactivateInEpost' === value.name ) {

            this.deactivateInEpost( {
                url: value.url,
                confirmation: value.confirmation,
                onSubmitUrl: get( value.config, 'url' )
            } );
        } else if ( 'impersonate' === value.name ) {
            this.impersonateMember( {
                url: value.url
            } );
        } else if ( 'ePostNotes' === value.name ) {
            this.epostMSRNotes( {
                url: value.url
            } );
        } else if ( 'reactivateEpost' === value.name ) {

            // this.props.history.push( {
            //     pathname: DASHBOARD_SUB_ROUTES.REACTIVATE_EPOST,
            //     state: { memberId: get( this.props.location, 'state.memberId', undefined ), patientId:  get( this.props.location, 'state.patientId', undefined ) }
            // } );
            this.reactivateInEpost( {
                // url: value.url,
                // confirmation: value.confirmation,
                // onSubmitUrl: get( value.config, 'url' )
            } );
        } else if ( 'enrollToEpost' === value.name ) {

            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.ENROLL_TO_EPOST,
                search: queryString.stringify( {
                    
                    origin: 'member-management'
                } ),
                state: resolveMemberDetailSelection( this.props.location.state, {
                    memberId: getAppSyncData( 'memberId' ),
                    patientId: getAppSyncData( 'patientId' ),
                    liveFallback: getAppSyncData( 'liveFallback' )
                } )
            } );

        } else if ( 'memberBusinessProcessTodo' === value.name ) {
            this.businessProcessTodo( {
                action: value
            } );
        } else if ( 'priorAuthorization' === value.name ) {
            this.fetchPriorAuthorization( {
                url: value.url
            } );

        } else if ( 'outboundCalls' === value.name ) {
            this.fetchOutboundCalls( {
                url: value.url
            } );

        } else if ( 'websiteIssues' === value.name ) {
            this.fetchWebsiteIssues( {
                url: value.url
            } );
        } else if ( 'memberServiceEscalation' === value.name ) {
            this.memberServicesEscalations( {
                action: value
            } );
        } else if ( 'memberEscalationCall' === value.name ) {
            this.fetchMemberEscalationCall( {
                url: value.url
            } );
        } else if ( 'materialMailOut' === value.name ) {
            this.fetchMaterialMailOut( {
                url: value.url
            } );
        } else if ( 'statusOfClaim' === value.name ) {
            this.fetchStatusOfClaim( {
                url: value.url
            } );
        } else if ( 'addInsurance' === value.name ) {
            this.fetchAddInsuranceDiscountCardTodoInfo( {
                url: value.url
            } );
        } else if ( 'patientNotes' === value.name ) {
            this.setPatientNotes( {
                url: value.url
            } );
        } else if ( 'cmsAction' === value.name ) {
            this.fetchCmsWidgetInfo( {
                url: value.url
            } );
        } else if ( 'personalId' === value.name ) {
            this.fetchPersonalIdInfo( {
                url: value.url
            } );
        } else if ( 'serviceNowAction' === value.name ) {
            this.fetchServiceNowForm( {
                url: value.url
            } );
        } else if ( 'languagePreference' === value.name ) {
            this.fetchLangPreferenceWidgetInfo( {
                url: value.url
            } );
        }else if ( 'ivrPin' === value.name ) {
            this.fetchivrPin( {
                url: value.url
            } );
        }else if ( 'creditAdd' === value.name ) {
            this.addCreditAction( {
                url: value.url
            } );
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.memberDetailAssistiveActionsNavWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberDetailAssistiveActionsNavWidgetView
                isFetching={this.state.isFetching}
                {...get( this.state, 'componentData', {} )}
                onActionClick={this.handleActionClick}
            />
        );
    }
}

// set display name
_MemberDetailAssistiveActionsNavWidget.displayName = '_MemberDetailAssistiveActionsNavWidget';

// set default props
_MemberDetailAssistiveActionsNavWidget.defaultProps = {};

// prop types
_MemberDetailAssistiveActionsNavWidget.propTypes = {};


/*********************************************/

// export with router
export const MemberDetailAssistiveActionsNavWidget = withRouter( _MemberDetailAssistiveActionsNavWidget );
